import type { Card } from '../types'
import type { DraftTouch } from '../utils/cardEditorFields'

/**
 * The newest server answer this client holds for a card, and whether a write to
 * that card is still in flight.
 *
 * Why this exists (docs/diagnostics/deck-save-visibility-window-2026-08-11.md).
 * A card write is a full replace, and the editing surface used to seed its draft
 * from the *list* query's cache — a copy that is, by construction, one round trip
 * behind the write that has just been issued. Re-entering a card inside that
 * window seeded a draft without the field the previous write had added, and the
 * next edit sent that draft back, deleting the field. Both requests returned 200
 * and there is no undo.
 *
 * Two facts close it, and neither of them lives in a React tree:
 *
 *  • **The write's own response is authoritative and was being thrown away.**
 *    `PATCH /cards/{id}` returns the whole fresh card; the client discarded it
 *    and asked the list for the same information a moment later. Recorded here,
 *    it is available to the very next mount — with no round trip at all — which
 *    is the whole of the "write resolved, list has not refetched" half of the
 *    window.
 *  • **A write in flight is a fact about the card, not about the component that
 *    issued it.** The commonest way to finish editing a card unmounts the
 *    surface that was editing it, so the pending write outlives its author. A
 *    module-level registry is what lets the *next* mount of the same card know
 *    that a payload built right now would be built from a picture that is about
 *    to be superseded.
 *
 * Deliberately not a React Query cache entry. The list cache is the thing that
 * is behind; writing the response into it would be optimistic list painting,
 * which the fix brief rules out as narrowing the window rather than closing it,
 * and it would make a safety property a side effect of a display concern.
 *
 * Deliberately not persisted. Everything here is about a window measured in
 * round trips within one page's lifetime; surviving a reload would mean holding
 * a "newest" answer that a fresh load's own read supersedes.
 */

interface Snapshot {
  card: Card
  /** Monotonic per-tab ordinal. Breaks ties when two answers carry the same
   * `updated_at` — the server's timestamp has finite resolution and two writes
   * can land inside it, in which case the later-recorded one is the later one. */
  seq: number
}

const snapshots = new Map<string, Snapshot>()
const pending = new Map<string, Promise<void>>()
const listeners = new Map<string, Set<() => void>>()
let counter = 0

/*
 * ── The editing draft, published so the list can show it (ADR-072) ───────────
 *
 * Everything above is server answers. What follows is the one copy of a change
 * the person has made and the server has not answered for yet — and it is
 * emphatically NOT a second copy kept alongside the draft, which ADR-072
 * forbids in as many words. It is the draft itself, put somewhere a sibling
 * component can read it, because the surface that owns the draft and the list
 * that has to show the same change are siblings in the tree and module scope is
 * the only place both can reach.
 *
 * The alternative — writing the change into the deck list's query cache on
 * mutate — is what ADR-071 rejected and ADR-072 keeps rejecting: it would put a
 * second, independently-evolving copy of the card in the cache, which is
 * exactly the thing that could then disagree with the draft.
 *
 * A published draft is superseded by the answer to the write that carried it,
 * and by nothing else — see `recordCardSnapshot`. That is what stops it from
 * becoming a stale copy that outlives its own reason to exist.
 */

interface Draft {
  card: Card
  /**
   * What the author has changed, carried with the change itself.
   *
   * This travels with the draft because the draft now outlives the mount that
   * made it. Reconciliation leaves a touched field alone and may refresh an
   * untouched one (ADR-071 Decision 2, unchanged) — a rule written when a draft
   * was always seeded from a server copy, so "untouched in this mount" and "not
   * authored yet" were the same statement. They stop being the same once a
   * mount can seed from an unconfirmed change made in a previous one: without
   * this, the next mount would report the author's own unsaved edit as
   * untouched, and reconciliation would dutifully replace it with the server's
   * older copy — losing the change on re-entry, which is the display half of
   * the very defect ADR-071 closed on the wire.
   *
   * Note the direction. This makes reconciliation protect MORE, never less: no
   * field that was protected before becomes overwritable, and nothing about
   * which side wins a conflict changes.
   */
  touch: DraftTouch
  /** Bumped on every publication. Compared against the value captured when a
   * write was issued, which is how an answer knows whether it covers everything
   * that has been published or whether the author has moved on since. */
  seq: number
}

const drafts = new Map<string, Draft>()
/** `drafts.get(id).seq` as it stood when the in-flight write was issued. */
const draftSeqAtWrite = new Map<string, number>()
/** Cards whose last write failed, and whose change is therefore standing
 * unsaved. The mark this drives is the only one an edit ever carries — an
 * ordinary unconfirmed change is indistinguishable from a confirmed one
 * (ADR-072 Decision 3). */
const failedWrites = new Set<string>()
let publishCounter = 0

function stamp(card: Card): number {
  const t = Date.parse(card.updated_at ?? '')
  return Number.isNaN(t) ? 0 : t
}

function notify(cardId: string) {
  const set = listeners.get(cardId)
  if (!set) return
  // Copied before iterating: a listener may unsubscribe itself in response.
  for (const fn of [...set]) fn()
}

/**
 * Record a server answer for a card. Keeps whichever answer is newer, so an
 * out-of-order arrival (a slow write resolving after a fast one) cannot move the
 * record backwards.
 */
export function recordCardSnapshot(card: Card): void {
  if (!card?.id) return
  const existing = snapshots.get(card.id)
  if (existing && stamp(existing.card) > stamp(card)) return
  snapshots.set(card.id, { card, seq: ++counter })

  // This answer supersedes the published draft exactly when it is the answer to
  // everything that had been published — i.e. nothing has been published since
  // the write went out. If the author has changed something in the meantime,
  // the draft stands: it holds work this answer does not describe, and a
  // deferred commit is already on its way to send it (ADR-071 Decision 3).
  //
  // Dropping it here rather than leaving it to the editor is what covers the
  // unmounted case, which is the common one: the gesture that finishes a card
  // is usually the gesture that closes it, so by the time the answer lands
  // there is frequently no editor left to retract anything.
  const draft = drafts.get(card.id)
  const coversEverythingPublished =
    (draft?.seq ?? -1) <= (draftSeqAtWrite.get(card.id) ?? -1)
  if (draft && coversEverythingPublished) {
    drafts.delete(card.id)
    draftSeqAtWrite.delete(card.id)
  }
  // The mark's clearing condition is "a write for this card landed, covering
  // everything published up to it" (ADR-073 Decision 1) — a condition about the
  // WRITE, not about whether a draft happens to still be standing when the
  // answer arrives. It used to be evaluated inside the branch above, which
  // silently added "…and there is still a draft to drop": a draft retracted
  // before the answer (the author having brought their edit back into agreement
  // with the server, `retractCardDraft`) left the mark with no reachable
  // clearing path at all, since a draft that agrees with the server also owes
  // no further write to clear it with. Same rule, evaluated where it was
  // written — not a second clearing path.
  if (coversEverythingPublished) failedWrites.delete(card.id)
  notify(card.id)
}

/**
 * Publish the editing draft for a card — what the person has changed and the
 * server has not answered for.
 *
 * Called by the surface that owns the draft, on every change to it, and only
 * while the draft differs from what the server is known to hold. A draft that
 * agrees with the server is not published (and is retracted if it was), so this
 * map holds unconfirmed authoring and nothing else — which is what keeps it
 * from quietly becoming a rival cache of cards nobody is editing.
 */
export function publishCardDraft(cardId: string, card: Card, touch: DraftTouch): void {
  if (!cardId) return
  drafts.set(cardId, {
    card,
    // Copied, not referenced: the caller's sets go on being mutated as the
    // author keeps typing, and a published draft must describe the change it
    // was published with.
    touch: { term: touch.term, fields: new Set(touch.fields), removed: new Set(touch.removed) },
    seq: ++publishCounter,
  })
  notify(cardId)
}

/**
 * Withdraw a published draft — the change it held is no longer unconfirmed.
 *
 * The failure mark goes with it, and this is the one case where something other
 * than a landed write clears it (ADR-074 amends ADR-073 Decision 1). It is not
 * a faster path to clearing a mark that is still true: retraction means the
 * draft has come to agree with what the server holds, so the change the mark
 * describes no longer exists to be unsaved. Leaving the mark up would report a
 * failed save of text that is no longer different from the server's — and it
 * could never come down, because a draft that agrees with the server owes no
 * write, and a landed write was the only other way out.
 */
export function retractCardDraft(cardId: string): void {
  if (!cardId || !drafts.delete(cardId)) return
  draftSeqAtWrite.delete(cardId)
  failedWrites.delete(cardId)
  notify(cardId)
}

/**
 * What the person should be looking at for this card: their own unconfirmed
 * change if there is one, otherwise the freshest server answer.
 *
 * This is the whole of ADR-072 Decision 5 — one authority for what is on
 * screen, on the editing surface and in the list alike, from the moment a
 * change is made until it is reconciled. Callers that must reason about what
 * the SERVER holds (reconciliation, and only reconciliation) use
 * `freshestCard`; everything that renders uses this.
 */
export function visibleCard(card: Card): Card {
  const draft = card?.id ? drafts.get(card.id) : undefined
  return draft ? draft.card : freshestCard(card)
}

/** What the author had changed when this card's draft was published, so a mount
 * that seeds from that draft inherits it rather than reporting the author's own
 * unsaved work as untouched. Null when there is no unconfirmed change. */
export function getCardDraftTouch(cardId: string): DraftTouch | null {
  return (cardId && drafts.get(cardId)?.touch) || null
}

/**
 * Record that this card's write failed, so the change it carried can be marked
 * as standing unsaved.
 *
 * Cleared only by an answer that supersedes the draft (above) — i.e. by the
 * change actually landing. Nothing here retries: the mark and the report are
 * what the person acts on, and the retry is theirs to ask for (ADR-072
 * Decision 4, which does not reopen ADR-071's ban on automatic retries).
 */
export function markCardWriteFailed(cardId: string): void {
  if (!cardId || failedWrites.has(cardId)) return
  failedWrites.add(cardId)
  notify(cardId)
}

/** Is this card's change standing unsaved after a failed write? */
export function cardWriteFailed(cardId: string): boolean {
  return !!cardId && failedWrites.has(cardId)
}

export function getCardSnapshot(cardId: string): Card | null {
  return snapshots.get(cardId)?.card ?? null
}

/**
 * The freshest copy of a card this client knows about: the passed-in one (from
 * a query cache) or a recorded write response, whichever carries the later
 * `updated_at`. A tie goes to the recorded answer — a list read that raced a
 * write is exactly the case this is here for.
 */
export function freshestCard(card: Card): Card {
  const snap = card?.id ? snapshots.get(card.id) : undefined
  if (!snap) return card
  return stamp(snap.card) >= stamp(card) ? snap.card : card
}

/** Called whenever this card's recorded answer changes. */
export function subscribeCardSnapshot(cardId: string, fn: () => void): () => void {
  if (!cardId) return () => {}
  let set = listeners.get(cardId)
  if (!set) { set = new Set(); listeners.set(cardId, set) }
  set.add(fn)
  return () => {
    set!.delete(fn)
    if (!set!.size) listeners.delete(cardId)
  }
}

/**
 * Register a write to `cardId` as in flight, and answer with a promise that
 * settles — never rejects — once it has finished *and* its answer has been
 * recorded above.
 *
 * The never-rejects part is what callers depend on: a deferred commit resumes
 * on failure exactly as it does on success, because a failed write leaves the
 * server holding whatever it held before and the draft is then the only copy of
 * the author's work.
 */
export function beginCardWrite<T extends Card>(cardId: string, write: Promise<T>): Promise<T> {
  const settled = write.then(() => {}, () => {})
  pending.set(cardId, settled)
  // What this write is carrying, in publication terms. Captured here, at issue
  // time, so its answer can tell "this covers everything published" from "the
  // author has changed something since" without either the editor or the
  // answer having to compare content (ADR-072 Decision 2 — the answer
  // reconciles through the draft; it never repaints over newer authoring).
  draftSeqAtWrite.set(cardId, drafts.get(cardId)?.seq ?? -1)
  // Registered before any caller's own continuation, so by the time a deferred
  // commit resumes the registry no longer claims a write is in flight and the
  // retry cannot defer against itself.
  void settled.then(() => {
    if (pending.get(cardId) === settled) pending.delete(cardId)
  })
  return write
}

/** The in-flight write to this card, if any. Settles without rejecting. */
export function pendingCardWrite(cardId: string): Promise<void> | null {
  return (cardId && pending.get(cardId)) || null
}

/** Test seam — a fresh tab has none of this. */
export function resetCardSnapshots(): void {
  snapshots.clear()
  pending.clear()
  listeners.clear()
  drafts.clear()
  draftSeqAtWrite.clear()
  failedWrites.clear()
  counter = 0
  publishCounter = 0
}
